<template>
  <div>
    <h2 :class="$style.title">
      {{ title }}
    </h2>
    <table
      id="table"
      :class="$style.table"
    >
      <thead :class="$style.header">
        <tr>
          <th
            v-for="column in columns"
            :key="column.field"
            :style="{
              width: `${column.width}%`,
            }"
            @click="onColumnClick(column)"
          >
            {{ column.name }}
            <img
              v-if="sortedColumn === column.field"
              src="@/assets/images/icons/table/sort-arrow.svg"
              :class="[
                $style.tdInline,
                $style.arrow,
                {
                  [$style.asc]: !isSortAscending,
                },
              ]"
            >
          </th>
        </tr>
      </thead>
      <tbody v-if="tableData.length">
        <tr
          v-for="(row, idx) in tableData"
          :key="idx"
          :ref="`${tableName}-${row.id}`"
          :class="{ [$style.highlight]: row.id === rowActiveId }"
        >
          <td
            v-for="column in columns"
            :key="column.field"
          >
            <slot
              v-if="hasDataColumnSlot(column.field)"
              :name="`column-${column.field}`"
              :row="row"
            />
            <template v-else-if="column.field === 'dates'">
              <table :class="$style.workTable">
                <tbody>
                  <template v-if="!isEmpty(row.reportData.datesLate)">
                    <tr
                      v-for="[date, value] in Object.entries(row.reportData.datesLate)"
                      :key="date"
                    >
                      <td>{{ date }}</td>
                      <td>{{ convertSecondsToDays(value) }}</td>
                      <td>{{ $t('reports.lateness') }}</td>
                    </tr>
                  </template>
                  <template v-if="!isEmpty(row.reportData.datesEarly)">
                    <tr
                      v-for="([date, value], index) in Object.entries(row.reportData.datesEarly)"
                      :key="`${date}-${index}`"
                    >
                      <td>{{ date }}</td>
                      <td>{{ convertSecondsToDays(value) }}</td>
                      <td>{{ $t('reports.earlyDeparture') }}</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </template>
            <template v-else-if="column.field === 'reasonString'">
              <span
                v-for="reasonId in row.reportData.reasonIds"
                :key="reasonId"
                :class="{ [$style.clickable]: clickable }"
                @click="openNotif(row.name, reasonId)"
              >
                {{ getReasonIdValue(column.field, reasonId) }}
              </span>
            </template>
            <template v-else>
              <span
                :class="{ [$style.clickable]: clickable && column.field === 'name' }"
                @click="openNotif(row.name)"
              >
                {{ getValue(row.reportData, column.field) }}
              </span>
            </template>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td :colspan="columns.length">
            {{ $t('table.noData') }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
  import { convertSecondsToDays } from '@/helpers/format-date';
  import { isEmpty } from 'lodash';
  import dayjs from 'dayjs';

  export default {
    props: {
      columns: {
        type: Array,
        required: true,
      },
      data: {
        type: Array,
        required: true,
      },
      tableName: {
        type: String,
      },
      title: {
        type: String,
      },
      clickable: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      sortedColumn: null,
      rowActiveId: null,
      isSortAscending: true,
    }),
    computed: {
      dateRange() {
        return this.$store.getters['filters/formattedDateRange'];
      },
      tableData() {
        const { sortedColumn, isSortAscending } = this;

        const sortedColumnObject = this.columns.find((i) => i.field === sortedColumn);

        if (
          sortedColumnObject &&
          this.hasDataColumnSlot(sortedColumn) &&
          typeof sortedColumnObject.sortFn === 'function'
        ) {
          const data = [...this.data].sort(sortedColumnObject.sortFn(isSortAscending));
          return data;
        }

        const data = [...this.data].sort(this.compare);

        return data;
      },
    },
    created() {
      const currentSorted = this.columns.find((item) => item?.sorted);
      if (currentSorted) this.sortedColumn = currentSorted.field;
    },
    methods: {
      openNotif(name, reasonId = null) {
        if (!this.clickable) return;

        const query = {
          name,
          reasonId,
          dateFrom: dayjs(this.dateRange.dateFrom).format('YYYY-MM-DD HH:mm:ss'),
          dateTo: dayjs(this.dateRange.dateTo).format('YYYY-MM-DD HH:mm:ss'),
        };
        const routeData = this.$router.resolve({
          name: 'notifications',
          query,
        });
        window.open(routeData.href, '_blank');
      },
      getReasonIdValue(field, reasonId) {
        return this.$t(`db.${field}.${reasonId}`);
      },
      getValue(reportData, field) {
        const { datesLate, datesEarly, datesOut } = reportData;
        const mappings = {
          quantity: () => Object.keys(datesLate).length + Object.keys(datesEarly).length,
          datesOut: () => (!isEmpty(datesOut) ? Object.keys(datesOut).join(', ') : ''),
        };
        return mappings[field] ? mappings[field]() : reportData[field];
      },
      scrollToRow(rowId) {
        this.$nextTick(() => {
          const refKey = `${this.tableName}-${rowId}`;
          const currentRef = this.$refs[refKey];
          const el = Array.isArray(currentRef) ? currentRef[0] : currentRef;

          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }

          setTimeout(() => {
            if (typeof window !== 'undefined') {
              window.scrollBy({
                top: -120,
                left: 0,
                behavior: 'smooth',
              });
            }
          }, 300);
        });
        this.rowActiveId = rowId;
        if (this.rowActiveId) {
          setTimeout(() => {
            this.rowActiveId = null;
          }, 10000);
        }
      },
      convertSecondsToDays(secNum) {
        return convertSecondsToDays(secNum);
      },
      isEmpty(object) {
        return isEmpty(object);
      },
      compare(a, b) {
        a = a[this.sortedColumn];
        b = b[this.sortedColumn];

        if (typeof a === 'string' && typeof b === 'string') {
          a = a.toLowerCase();
          b = b.toLowerCase();
        }

        if (this.isSortAscending) {
          if (a > b) {
            return -1;
          }

          if (a < b) {
            return 1;
          }
          return 0;
        }

        if (a < b) {
          return -1;
        }

        if (a > b) {
          return 1;
        }

        return 0;
      },
      hasDataColumnSlot(field) {
        return !!this.$scopedSlots[`column-${field}`];
      },
      onColumnClick(column) {
        if (!column?.sorted) return;
        if (column.field === this.sortedColumn) {
          this.isSortAscending = !this.isSortAscending;
        } else {
          this.sortedColumn = column.field;
          this.isSortAscending = true;
        }
      },
    },
  };
</script>

<style lang="scss" module>
  .title {
    padding: 10px;
  }
  .table {
    width: 100%;
    margin-bottom: 40px;
    border-collapse: collapse;
    font-weight: 400;
    word-break: break-word;
    border: 1px solid $borderColor;
    td {
      border-right: 1px solid $borderColor;
      border-left: 1px solid $borderColor;
      align-items: center;
      color: $black;
      font-weight: 300;
      line-height: 14px;
      text-align: center;
      font-size: 14px;
      letter-spacing: 0px;
      padding: 7px 4px 4px 10px;
      &:last-child {
        max-width: 150px;
        text-align: start;
      }
    }
  }

  .header {
    background: #f5f5f5;
    height: 38px;
    position: sticky;
    z-index: 3;
    th {
      border-right: 1px solid $borderColor;
      border-left: 1px solid $borderColor;
      align-items: center;
      text-align: center;
      color: #232326;
      letter-spacing: -0.08px;
      font-size: 13px;
      padding: 10px 4px 10px 10px;
      cursor: pointer;
    }
  }

  .textCentr {
    text-align: center;
  }

  .workTable {
    border-collapse: collapse;
    border-spacing: 0;
    margin: 0;
    padding: 0;
    width: max-content;
    max-width: 100%;
  }

  .workTable th,
  .workTable td {
    padding: 0;
    margin: 0;
    border: none;
    text-align: left;
    vertical-align: top;
    background: none;
    white-space: nowrap;
    padding: 2px 10px 2px;
  }
  .highlight {
    animation: flash 10s ease;
  }

  @keyframes flash {
    0% {
      background: $dark-gray;
    }
    100% {
      background: transparent;
    }
  }

  .clickable {
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
    &.active {
      text-decoration: underline;
    }
  }
  .tdInline {
    display: inline-flex;
  }

  .arrow {
    margin-left: 4px;

    &.asc {
      transform: rotate(180deg);
    }

    &.hidden {
      visibility: hidden;
    }
  }
</style>
